import React, { useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * BookingCalendar — shared calendar for Item and Case detail pages.
 *
 * Props:
 *   schedule      {Array}  – entries from /schedule/ endpoint
 *   loading       {bool}
 *   filter        {string} – 'upcoming' | 'active' | 'all'
 */
const TYPE_COLORS = {
  rental:  '#f76707', // orange
  project: '#206bc4', // blue
};

const BookingCalendar = ({ schedule = [], loading = false, filter = 'upcoming' }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const today = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  }, []);

  const events = useMemo(() => {
    const filtered = schedule.filter(entry => {
      if (filter === 'all') return true;
      const end = entry.end_date ? new Date(entry.end_date) : null;
      const start = entry.start_date ? new Date(entry.start_date) : null;
      if (filter === 'active') return start && end && start <= today && end >= today;
      // upcoming: end is in the future or no end set
      return !end || end >= today;
    });

    return filtered.map(entry => {
      // FullCalendar end date is exclusive — add 1 day for all-day events
      let endDate = entry.end_date;
      if (endDate) {
        const d = new Date(endDate);
        d.setDate(d.getDate() + 1);
        endDate = d.toISOString().slice(0, 10);
      }

      const color = entry.status_color || TYPE_COLORS[entry.type] || '#6c757d';

      return {
        id: `${entry.type}-${entry.id}`,
        title: `${entry.name} ×${entry.quantity}`,
        start: entry.start_date || undefined,
        end: endDate || undefined,
        backgroundColor: color,
        borderColor: color,
        textColor: '#fff',
        extendedProps: { link: entry.link, type: entry.type },
      };
    });
  }, [schedule, filter, today]);

  const handleEventClick = (info) => {
    const link = info.event.extendedProps.link;
    if (link) navigate(link);
  };

  if (loading) {
    return (
      <div className="py-5 text-center text-muted">
        <Spinner size="sm" className="me-2" />
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="p-3">
      {/* Legend */}
      <div className="d-flex gap-3 mb-3 flex-wrap">
        <span className="d-flex align-items-center gap-1 small">
          <span className="badge" style={{ backgroundColor: TYPE_COLORS.rental }}>&nbsp;</span>
          {t('warehouses.items.typeRental')}
        </span>
        <span className="d-flex align-items-center gap-1 small">
          <span className="badge" style={{ backgroundColor: TYPE_COLORS.project }}>&nbsp;</span>
          {t('warehouses.items.typeProject')}
        </span>
      </div>

      <FullCalendar
        plugins={[dayGridPlugin, interactionPlugin]}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,dayGridYear',
        }}
        initialView="dayGridMonth"
        editable={false}
        dayMaxEvents={4}
        weekends={true}
        events={events}
        eventClick={handleEventClick}
        height="auto"
        locale={i18n.language}
        buttonText={{
          today: t('calendar.today', 'Today'),
          month: t('calendar.month', 'Month'),
          year: t('calendar.year', 'Year'),
        }}
        moreLinkText={count => `+${count}`}
        noEventsText={t('calendar.noEvents', 'No bookings')}
        eventCursor="pointer"
      />
    </div>
  );
};

export default BookingCalendar;
